import { Injectable } from '@angular/core';
import * as L from 'leaflet'; // ensure this is at top
import { geoserverUrl } from './constants';

export interface FeedUI {           // the hint text
  showFilterIcon: boolean;   
}

export interface FeedViewModel {
  sections: FeedSection[];
  ui: FeedUI;
}

export interface FeedLine {
  text: string;
  legendColor?: string;
}
export interface FeedSection {
  show: boolean;
  className: string;
  style?: { [k: string]: string };
  items: FeedLine[];
}
export interface BuildFeedSectionsMeta {
  featureCount?: number;
  showCountRow?: boolean;   // <= only override when this is true
}

export interface BuildFeedSectionsMeta { featureCount?: number }

@Injectable({ providedIn: 'root' })
export class HelperService {

  private safeAssign(
  label: string,
  value: unknown,
  suffix = '',
  extraVisible?: boolean,     // 👈 new flag
  labelWhenEmpty?: string     // 👈 optional override label when no value
): string {
  const hasValue = value !== null && value !== undefined && value !== '';
  if (hasValue) {
    return `${label} : ${value}${suffix}`;
  }
  if (extraVisible) {
    return labelWhenEmpty ?? label; // show label even without value
  }
  return '';
}


private pushIf(
    items: FeedLine[],
    label: string,
    value: unknown,
    opts?: 
    { suffix?: string; 
      color?: string, 
      visibleIf?: boolean;   
      labelWhenEmpty?: string; 
    }
  ) {
    const text = this.safeAssign(label, value, opts?.suffix ?? '',  opts?.visibleIf, opts?.labelWhenEmpty);
    if (text) items.push({ text, legendColor: opts?.color });
  }


getFormattedArea(data: any, metric: string): string {
  if (data == null || data === 'N/A' || data === '') return '';
  const raw = String(data);
  // grab the first number (handles NBSP, commas, etc.)
  const m = raw.match(/[+-]?\d+(?:[.,]\d+)?/);
  if (!m) return '';
  const numberPart = Math.ceil(parseFloat(m[0].replace(/,/g, '')));
  const formatted = numberPart.toLocaleString('en-US');
  return `${formatted} ${metric}`;
}

buildFeedSections(config: { type?: string, color?: string }, filteredCollection: any): FeedViewModel {
    const sections: FeedSection[] = []; {
    const defaultSection: FeedSection = {
      show: false,
      className: 'feed-display feed-class',
      items: [],
    };

    const blackSection: FeedSection = {
      show: false,
      className: 'feed-display oil-class',
      style: { 'background-color': 'black', padding: '7px' },
      items: [],
    };
     const ui: FeedUI = {
      showFilterIcon: false,
    };


    switch (config?.type) {
      case 'Tree counting':
        {
        this.buildKeyValueSection(defaultSection, filteredCollection, config?.color ?? '', 'Tree crown area', config);
        ui.showFilterIcon = false;  
        break;
      }
       case 'Vessel': {
         this.buildKeyValueSection(defaultSection, filteredCollection, config?.color ?? '', 'Vessel Count', config);
         ui.showFilterIcon = true;  
        break;
      }
       case 'Transshipment': {
         this.buildKeyValueSection(defaultSection, filteredCollection, config?.color ?? '', 'Transshipment Count', config);
         ui.showFilterIcon = true;  
        break;
      }
      case 'Deforestation': {
        this.buildKeyValueSection(defaultSection, filteredCollection, config?.color ?? '', 'Deforested area', config);
        ui.showFilterIcon = false;  
        break;
      }

      case 'Oil': {
       this.buildKeyValueSection(defaultSection, filteredCollection, config?.color ?? '', "Oil spill area", config);
       ui.showFilterIcon = false;  
      break;
      }
      case 'Path-SAR': {
        const defaultColor = '#ba5a06';
        this.buildKeyValueSection(defaultSection, filteredCollection, defaultColor, "Path (unpaved road) detection - SAR", config);
        break;
      }
      case 'Plantation-SAR': {
        const defaultColor = '#e2e90bff';
        this.buildKeyValueSection(defaultSection, filteredCollection, defaultColor, "Blank spot detection - SAR", config);
        break;
      }
      case 'Flood': {
        this.buildKeyValueSection(defaultSection, filteredCollection, config?.color ?? '', "Flood extent", config);
        ui.showFilterIcon = false;  
        break;
      }
      case 'Path': {
        this.buildKeyValueSection(defaultSection, filteredCollection, config?.color ?? '', "Path (Unmetalled road)", config);
        ui.showFilterIcon = false;  
        break;
      }

      case 'Plantation': {
        const defaultColor = '#e2e90bff';
        this.buildKeyValueSection(defaultSection, filteredCollection, defaultColor, "Plantation area", config);
        break;
      }
    }

    if (defaultSection.show) sections.push(defaultSection);
    if (blackSection.show) sections.push(blackSection);

    return { sections, ui };
}
}



private readonly prettyLabel = (k: string) =>
  k.replace(/_/g, ' ').replace(/^./, c => c.toUpperCase());

buildKeyValueSection(
  section: FeedSection,
  filteredCollection: any,
  defaultColor: string,
  emptyFallbackLabel: string,  
  config?: { type?: string },      
) {

let entries: [string, any][] = [];

if (filteredCollection && typeof filteredCollection === 'object') {
  entries = Object.entries(filteredCollection);
} else {
  entries = [];
}
  // Empty -> show a single fallback row
if (entries.length === 0 ) {
    this.pushIf(section.items, emptyFallbackLabel, "", {
      color: defaultColor,
      visibleIf: true,
      labelWhenEmpty: emptyFallbackLabel,
    });
    section.show = true;
    return;
}


for (const [key, value] of entries) {
  // 🔹 CASE 1: value is an object with a `metric` -> expand it
  if (value && typeof value === 'object' && 'metric' in value) {
    const metric = value.metric;
    for (const [innerKey, innerValue] of Object.entries(value)) {
      if (innerKey === 'metric') continue; // skip metric itself

      const label = this.prettyLabel(innerKey); // e.g. plantable_area -> Plantable Area
      let display: string;

       if (config?.type === 'Oil') {
        display = this.getFormattedArea(innerValue, metric);
       } else if (config?.type === 'Plantation' || config?.type === 'Plantation-SAR') {
       display = this.roundWithUnit(innerValue, 2, metric);
       } else {
       display = String(value ?? '');
     }
      this.pushIf(section.items, label, display, {
        color: defaultColor,
        visibleIf: true,
        labelWhenEmpty: label,
      });
    }
    continue;
  }
  const label = this.prettyLabel(key);
  this.pushIf(section.items, label, value, {
    color: defaultColor,
    visibleIf: true,
    labelWhenEmpty: label,
  });
}
section.show = section.items.length > 0;
}

roundWithUnit(value: any, decimals: number = 2, metric: string): string {
  let rounded = parseFloat(value).toFixed(decimals);
  return `${rounded} ${metric}`;
}

public buildTooltipContent(
  props: Record<string, unknown> = {},
  rawLen?: unknown,
  centroidCoordinates?: [number, number] | number[] | string
): string {
  const esc = (v: any) =>
    String(v)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

  const loc =
    Array.isArray(centroidCoordinates)
      ? centroidCoordinates.join(', ')       // original precision, lon,lat order
      : (centroidCoordinates ?? '');
  const locParts = String(loc).split(',').map(p => p.trim());

  const conf = props?.['confidence'];        // bracket access fixes index-signature warning
  const confidenceLine = conf != null ? `<b>Confidence:</b> ${esc(conf)}<br>` : '';
  const lengthLine     = rawLen ? `<b>Vessel length:</b> ${esc(rawLen)}<br>` : '';
  const lng   = loc !== '' ? `<b>Lng:</b> ${esc(locParts[0])}<br>` : '';
  const lat   = loc !== '' ? `<b>Lat:</b> ${esc(locParts[1])}<br>` : '';

  return `
    <div style="background-color:black;color:white;padding:5px;max-width:350px;">
      ${confidenceLine}${lengthLine}${lng}${lat}
    </div>
  `;
}

public computeAnchorAndLocation(
    feature: any,
    latlng: L.LatLng
  ): { anchor: L.LatLng; centroidCoordinates: [number, number] } {
    const t = (window as any).turf;
    // defaults: mouse position if nothing else works
    let centroidCoordinates: [number, number] = [latlng.lng, latlng.lat]; // [lon, lat]
    let anchor: L.LatLng = latlng;

    try {
      if (feature?.geometry?.type === 'Point') {
        const coords = feature.geometry.coordinates as [number, number];
        if (Array.isArray(coords) && coords.length >= 2) {
          centroidCoordinates = [coords[0], coords[1]];
        }
      } else if (t?.pointOnFeature) {
        const pt = t.pointOnFeature(feature);
        const coords = pt?.geometry?.coordinates as [number, number] | undefined;
        if (Array.isArray(coords) && coords.length >= 2) {
          centroidCoordinates = [coords[0], coords[1]];
        }
      }
      // keep anchor aligned with what we display (GeoJSON is [lon, lat])
      anchor = L.latLng(centroidCoordinates[1], centroidCoordinates[0]);
    } catch {
      // fall back already set
    }
    return { anchor, centroidCoordinates };
}

normalizeConfig(input: any) {
  const src = input?.config ?? input ?? {};
  const { type, filterKey, class: cls, color } = src;
  return { type, filterKey, class: cls, color };
}

classCqlStrict(cls: unknown, field = 'class'): string {
  const f = `"${field}"`;
  const s = String(cls ?? '').trim().toLowerCase();
  if (!s) return '';

  if (s === 'ship') {
    return `strToLowerCase(${f}) = 'ship'`;
  }
  if (s === 'transhipment' || s === 'transshipment') {
    // accept both spellings present in datasets
    return `strToLowerCase(${f}) IN ('transhipment','transshipment')`;
  }
  return ''; // do NOT coerce anything else into transhipment
}

getFeedResultKeyForClass(feedType?: string): 'vessel_count' | 'transhipment_count' {
  const s = String(feedType ?? '').trim().toLowerCase();
  return s.includes('trans') ? 'transhipment_count' : 'vessel_count';
}

combineFilters(...parts: Array<string | undefined | null>): string {
  const wrapIfOr = (s: string) =>
    /\bOR\b/i.test(s) && !(s.trim().startsWith('(') && s.trim().endsWith(')'))
      ? `(${s.trim()})` : s.trim();
  const out: string[] = [];
  const seen = new Set<string>();
  for (const p of parts) {
    const s = (p ?? '').trim();
    if (!s) continue;
    const norm = s.replace(/\s+/g, ' ').toLowerCase();
    if (seen.has(norm)) continue;
    seen.add(norm);
    out.push(wrapIfOr(s));
  }
  return out.join(' AND ');
}

makeWfsParams(layerName: string){
 return new URLSearchParams({
    service: 'WFS',
    version: '2.0.0',
    request: 'GetFeature',
    typeName: layerName,                 
    outputFormat: 'application/json',
    srsName: 'EPSG:4326'
  });
}
  validateCoordinates(longitude, latitude) {
    // Check if values are numbers
    if (isNaN(longitude) || isNaN(latitude)) {
      return false;
    }
    
    // Check longitude range (-180 to 180)
    if (longitude < -180 || longitude > 180) {
      return false;
    }
    
    // Check latitude range (-90 to 90)
    if (latitude < -90 || latitude > 90) {
      return false;
    }
    
    return true;
  }

  createWmsLayer(
  map: L.Map,
  urlData: any,
  shapeFile: string,
  zIndex: number = 1000,
  isValueAddedProduct: boolean = false,
  baseUrlValue?: string,
  pane?: string  
): L.TileLayer.WMS | null {
  if (!urlData?.layer && !isValueAddedProduct) return null;
  const layerObj = isValueAddedProduct ? urlData : urlData.layer;
  const bounds = layerObj.bounds;
  const baseUrl = geoserverUrl;
  let layerName = layerObj.layerName;
  let bands = layerObj.bands;
  let style = layerObj.tiffStyle;

  // Handle shapes overlay if shapeFile is not "image"
  if (shapeFile !== 'image' && Array.isArray(layerObj.shapes) && layerObj.shapes.length > 0) {
    const shapeNames = layerObj.shapes.map((s: any) => s.layerName).join(',');
    const shapeStyles = layerObj.shapes.map((s: any) => s.style).filter(Boolean).join(',');

    layerName += ',' + shapeNames;
    if (shapeStyles) style += ',' + shapeStyles;
  }

  // Basic WMS params
  let params: any = {
    layers: layerName,
    format: 'image/png',
    transparent: true,
    zIndex: zIndex,
    pane: pane || 'tilePane'
  };

  if (style) params.styles = style;

  // Handle env (band min/max or multi-band)
  if (bands?.length === 1) {
    const { min, max } = bands[0];
    params.env = `minValue:${min};maxValue:${max}`;
  } else if (bands?.length > 1) {
    const env = bands
      .map((b: any, i: number) => {
        let e = `b${i + 1}:${b.band};`;
        if (b.min && b.max) e += `b${i + 1}minValue:${b.min};b${i + 1}maxValue:${b.max};`;
        return e;
      })
      .join('');
    params.env = env;
  }
  // Create & add WMS layer
  const wmsLayer = L.tileLayer.wms(baseUrl, params);
   map.fitBounds(bounds);
  wmsLayer.addTo(map);

  return wmsLayer;
}

}


